import React, { useState, useEffect } from "react";
import { useTable, usePagination, useGlobalFilter } from "react-table";
import FilterModal from "./FilterModel";
import EmployeeModal from "./EmployeeModal";
import DrugModal from "./DrugModal";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaStore, FaBuilding } from "react-icons/fa";
import {
  FiEdit3,
  FiTrash2,
  FiEye,
  FiUser,
  FiPlus,
  FiFilter,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiMapPin,
  FiPhone,
  FiMail,
  FiCalendar,
  FiDownload,
  FiX,
  FiGrid,
  FiList,
  FiClock,
  FiCheckCircle,
  FiPackage,
  FiUsers
} from "react-icons/fi";
import axios from "axios";
import "./Print.css";
import html2pdf from "html2pdf.js";
import Swal from "sweetalert2";

const OutletPage = () => {
  const navigate = useNavigate();
  const [outlets, setOutlets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [isDetailVisible, setIsDetailVisible] = useState(false);
  const [viewMode, setViewMode] = useState("table"); // "table" (list) or "grid"
  const [pageSize, setPageSize] = useState(10);
  const [userRole, setUserRole] = useState(null);
  const [showAccessDenied, setShowAccessDenied] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({});

  // Shift & Modal State
  const [isShiftVisible, setIsShiftVisible] = useState(false);
  const [shiftData, setShiftData] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  
  const [employees, setEmployees] = useState([]);
  const [employeesMeta, setEmployeesMeta] = useState(null);
  const [drugs, setDrugs] = useState([]);
  const [drugsMeta, setDrugsMeta] = useState(null);
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
  const [isDrugModalOpen, setIsDrugModalOpen] = useState(false);

  useEffect(() => {
    const role = (localStorage.getItem("role") || "").toLowerCase();
    setUserRole(role);
    if (role && !["owner", "admin", "superadmin", "manager"].includes(role)) {
      setShowAccessDenied(true);
    }
  }, []);

  const handleOpenEmployeeModal = () => {
    if (selectedOutlet) fetchEmployees(selectedOutlet, 1, "");
    setIsEmployeeModalOpen(true);
  };

  const handleOpenDrugModal = () => {
    if (selectedOutlet) fetchDrugs(selectedOutlet, 1, "");
    setIsDrugModalOpen(true);
  };

  const fetchEmployees = async (targetOutlet = selectedOutlet, page = 1, search = "") => {
    const outlet = targetOutlet || selectedOutlet;
    if (!outlet || !outlet.id) return;
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/employee?id_outlet=${outlet.id}&outlet_id=${outlet.id}&page=${page}&per_page=10${search ? `&search=${encodeURIComponent(search)}` : ""}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const resData = response.data;
      const list = Array.isArray(resData?.data)
        ? resData.data
        : Array.isArray(resData?.data?.data)
        ? resData.data.data
        : [];
      setEmployees(list);
      if (resData?.meta) {
        setEmployeesMeta(resData.meta);
      } else {
        setEmployeesMeta({
          current_page: page,
          last_page: Math.ceil((list.length || 0) / 10) || 1,
          total: list.length || 0,
          per_page: 10,
        });
      }
    } catch (error) {
      console.error("Error fetching employee:", error);
      setEmployees([]);
      setEmployeesMeta(null);
    }
  };

  const fetchDrugs = async (targetOutlet = selectedOutlet, page = 1, search = "") => {
    const outlet = targetOutlet || selectedOutlet;
    if (!outlet || !outlet.id) return;
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/drug?id_outlet=${outlet.id}&outlet_id=${outlet.id}&page=${page}&per_page=10${search ? `&search=${encodeURIComponent(search)}` : ""}`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const resData = response.data;
      const list = Array.isArray(resData?.data)
        ? resData.data
        : Array.isArray(resData?.data?.data)
        ? resData.data.data
        : [];
      setDrugs(list);
      if (resData?.meta) {
        setDrugsMeta(resData.meta);
      } else {
        setDrugsMeta({
          current_page: page,
          last_page: Math.ceil((list.length || 0) / 10) || 1,
          total: list.length || 0,
          per_page: 10,
        });
      }
    } catch (error) {
      console.error("Error fetching drugs:", error);
      setDrugs([]);
      setDrugsMeta(null);
    }
  };

  useEffect(() => {
    if (isDrugModalOpen && selectedOutlet) {
      fetchDrugs(selectedOutlet, 1, "");
    }
  }, [selectedOutlet, isDrugModalOpen]);

  useEffect(() => {
    if (isEmployeeModalOpen && selectedOutlet) {
      fetchEmployees(selectedOutlet, 1, "");
    }
  }, [selectedOutlet, isEmployeeModalOpen]);

  const fetchData = async (appliedFilters = {}) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!token) return;

      const params = new URLSearchParams({
        per_page: appliedFilters.per_page || 50,
        page: appliedFilters.page || 1,
        order_by: appliedFilters.order_by || "created_at",
        status: appliedFilters.status || "all",
      }).toString();

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/v1/outlet?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setOutlets(response.data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleApplyFilters = (appliedFilters) => {
    setFilters(appliedFilters);
    setIsFilterOpen(false);
    fetchData(appliedFilters);
  };

  const handleEdit = (outlet) => {
    navigate(`/enterprise/outlet/edit-outlet/${outlet.id}`);
  };

  const handleDelete = async (outlet) => {
    const result = await Swal.fire({
      title: `Hapus Outlet "${outlet.name}"?`,
      text: "Tindakan ini tidak dapat dibatalkan!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#6B7280",
      confirmButtonText: "Ya, Hapus!",
      cancelButtonText: "Batal",
      customClass: { popup: "rounded-3xl" },
    });

    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/outlet/${outlet.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Gagal menghapus outlet");
      }

      await Swal.fire({
        title: "Terhapus!",
        text: `Outlet "${outlet.name}" telah dihapus.`,
        icon: "success",
        timer: 1800,
        showConfirmButton: false,
        customClass: { popup: "rounded-3xl" },
      });

      if (selectedOutlet?.id === outlet.id) {
        setIsDetailVisible(false);
        setSelectedOutlet(null);
      }

      fetchData();
    } catch (error) {
      Swal.fire({
        title: "Gagal!",
        text: error.message,
        icon: "error",
        customClass: { popup: "rounded-3xl" },
      });
    }
  };

  const handleDetail = (outlet) => {
    setSelectedOutlet(outlet);
    setIsDetailVisible(true);
  };

  const closeDetail = () => {
    setIsDetailVisible(false);
    setSelectedOutlet(null);
  };

  // Shift Modal Logic
  const fetchPeriods = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/shift/period-shift`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      const data = await response.json();
      setPeriods(data.data || []);
      setSelectedPeriod(data.data[0]?.id || "");
    } catch (error) {
      console.error("Error fetching periods:", error);
    }
  };

  const fetchShifts = async () => {
    if (!selectedOutlet || !selectedPeriod) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/employee/shift?outlet_id=${selectedOutlet.id}&period_id=${selectedPeriod}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      const data = await response.json();
      if (data.success) {
        setShiftData(data.data || []);
      }
    } catch (error) {
      console.error("Error fetching shifts:", error);
    }
  };

  const showShiftDetail = async () => {
    setIsShiftVisible(true);
    await fetchPeriods();
  };

  const closeShiftDetail = () => {
    setIsShiftVisible(false);
    setShiftData([]);
  };

  const exportToPDF = async () => {
    if (!selectedOutlet) return;
    const outletName = selectedOutlet?.name || "Tidak Diketahui";
    const monthNames = [
      "Januari", "Februari", "Maret", "April", "Mei", "Juni",
      "Juli", "Agustus", "September", "Oktober", "November", "Desember"
    ];
    const selectedPeriodData = periods?.find((p) => String(p.id) === String(selectedPeriod));

    let periodeBulan = "Bulan Ini";
    let totalDays = 30;
    if (selectedPeriodData) {
      const monthNumber = parseInt(selectedPeriodData.month, 10);
      periodeBulan = `${monthNames[monthNumber - 1] || "Bulan"} ${selectedPeriodData.year}`;
      totalDays = new Date(selectedPeriodData.year, monthNumber, 0).getDate();
    }

    let tableHTML = `
      <div style="font-family: sans-serif; text-align: center; padding: 10px;">
        <h2 style="color: #0D9488; margin-bottom: 4px;">Jadwal Shift Karyawan Apotek Djaya Medika</h2>
        <h3 style="color: #4B5563; margin-top: 0;">Outlet: ${outletName} (${periodeBulan})</h3>
        <table style="width: 100%; border-collapse: collapse; text-align: center; font-size: 11px; margin-top: 15px;">
          <thead style="background-color: #0D9488; color: white;">
            <tr>
              <th style="padding: 6px; border: 1px solid #ccc;">No</th>
              <th style="padding: 6px; border: 1px solid #ccc;">Nama Karyawan</th>
    `;

    for (let i = 1; i <= totalDays; i++) {
      tableHTML += `<th style="padding: 4px; border: 1px solid #ccc;">${i}</th>`;
    }

    tableHTML += `</tr></thead><tbody>`;

    if (!shiftData || shiftData.length === 0) {
      tableHTML += `<tr><td colspan="${totalDays + 2}" style="padding: 12px; border: 1px solid #ccc;">Tidak ada data shift</td></tr>`;
    } else {
      shiftData.forEach((emp, index) => {
        tableHTML += `<tr>
          <td style="padding: 6px; border: 1px solid #ccc;">${index + 1}</td>
          <td style="padding: 6px; border: 1px solid #ccc; text-align: left;">${emp.full_name}</td>
        `;
        for (let i = 1; i <= totalDays; i++) {
          const formattedDate = `${selectedPeriodData.year}-${String(selectedPeriodData.month).padStart(2, "0")}-${String(i).padStart(2, "0")}`;
          const shift = emp.shifts?.find((s) => s.day === formattedDate);
          const shiftText = shift ? shift.shift?.code || "S" : "-";
          const bgColor = shift ? "#CCFBF1" : "#FFFFFF";
          tableHTML += `<td style="padding: 4px; border: 1px solid #ccc; background-color: ${bgColor}; font-weight: bold;">${shiftText}</td>`;
        }
        tableHTML += `</tr>`;
      });
    }

    tableHTML += `</tbody></table></div>`;

    const element = document.createElement("div");
    element.innerHTML = tableHTML;
    document.body.appendChild(element);

    html2pdf()
      .set({
        margin: 10,
        filename: `Shift_${outletName}_${periodeBulan}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
      })
      .from(element)
      .save()
      .then(() => {
        document.body.removeChild(element);
      });
  };

  // React Table setup
  const columns = React.useMemo(
    () => [
      {
        Header: "No",
        Cell: ({ row }) => <span className="font-semibold text-gray-500">{row.index + 1}</span>,
      },
      {
        Header: "Nama Outlet",
        accessor: "name",
        Cell: ({ row }) => (
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-teal-100 text-teal-700 font-bold flex items-center justify-center text-xs">
              <FaStore className="w-4 h-4" />
            </div>
            <div>
              <span className="font-bold text-gray-900 block">{row.original.name}</span>
              <span className="text-[11px] text-gray-400">{row.original.kota || "Kota -"}</span>
            </div>
          </div>
        ),
      },
      {
        Header: "Kota",
        accessor: "kota",
        Cell: ({ value }) => <span className="font-medium text-gray-700">{value || "-"}</span>,
      },
      {
        Header: "Telepon",
        accessor: "phone",
        Cell: ({ value }) => <span className="text-gray-600 font-mono text-xs">{value || "-"}</span>,
      },
      {
        Header: "Email",
        accessor: "email",
        Cell: ({ value }) => <span className="text-gray-600 text-xs">{value || "-"}</span>,
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => {
          const isAct = value === "active" || value === "Aktif" || value === "Active";
          return (
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${
                isAct
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-rose-50 text-rose-700 border border-rose-200"
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${isAct ? "bg-emerald-500" : "bg-rose-500"}`} />
              {isAct ? "Aktif" : "Non-Aktif"}
            </span>
          );
        },
      },
      {
        Header: "Aksi",
        Cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleDetail(row.original)}
              className="p-2 rounded-xl bg-teal-50 hover:bg-teal-100 text-teal-600 transition-all hover:scale-105"
              title="Lihat Detail"
            >
              <FiEye className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleEdit(row.original)}
              className="p-2 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-600 transition-all hover:scale-105"
              title="Edit Outlet"
            >
              <FiEdit3 className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDelete(row.original)}
              className="p-2 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-600 transition-all hover:scale-105"
              title="Hapus Outlet"
            >
              <FiTrash2 className="w-4 h-4" />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    setGlobalFilter,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state: { pageIndex, globalFilter },
  } = useTable(
    { columns, data: outlets, initialState: { pageSize } },
    useGlobalFilter,
    usePagination
  );

  const activeOutletsCount = outlets.filter(
    (o) => o.status === "active" || o.status === "Aktif" || o.status === "Active"
  ).length;

  return (
    <div className="space-y-6 pb-12">
      {/* 1. TOP METRIC SUMMARY BAR */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-5"
      >
        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Total Outlet
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900">
              {isLoading ? "..." : outlets.length}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-teal-50 text-teal-600 border border-teal-100">
            <FaStore className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Outlet Aktif
            </p>
            <h2 className="text-3xl font-extrabold text-emerald-600">
              {isLoading ? "..." : activeOutletsCount}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-emerald-50 text-emerald-600 border border-emerald-100">
            <FiCheckCircle className="w-6 h-6" />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-5 shadow-xl shadow-gray-200/50 border border-gray-100 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">
              Manajer Beroperasi
            </p>
            <h2 className="text-3xl font-extrabold text-blue-600">
              {isLoading
                ? "..."
                : outlets.filter((o) => o.manager || o.manager_id).length}
            </h2>
          </div>
          <div className="p-3.5 rounded-2xl bg-blue-50 text-blue-600 border border-blue-100">
            <FiUser className="w-6 h-6" />
          </div>
        </div>
      </motion.div>

      {/* 2. MAIN CONTROLS & ACTION BAR */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white p-6 rounded-3xl shadow-xl shadow-gray-200/50 border border-gray-100 space-y-6"
      >
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Live Search */}
          <div className="relative w-full md:w-80">
            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Cari nama outlet, kota, email..."
              value={globalFilter || ""}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-end">
            {/* View Mode Toggle */}
            <div className="flex items-center p-1 bg-gray-100 rounded-2xl border border-gray-200/80">
              <button
                onClick={() => setViewMode("grid")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                  viewMode === "grid"
                    ? "bg-white text-teal-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-900"
                }`}
              >
                <FiGrid className="w-3.5 h-3.5" />
                <span>Grid</span>
              </button>
              <button
                onClick={() => setViewMode("table")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                  viewMode === "table"
                    ? "bg-white text-teal-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-900"
                }`}
              >
                <FiList className="w-3.5 h-3.5" />
                <span>Tabel</span>
              </button>
            </div>

            {/* Filter Modal Toggle */}
            <button
              onClick={() => setIsFilterOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-xs font-semibold rounded-2xl transition-all shadow-sm"
            >
              <FiFilter className="w-4 h-4 text-teal-600" />
              <span>Filter</span>
            </button>

            {/* Create Outlet CTA */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/enterprise/outlet/create-outlet")}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white text-xs font-bold rounded-2xl shadow-lg shadow-teal-600/20 transition-all"
            >
              <FiPlus className="w-4 h-4" />
              <span>Tambah Outlet</span>
            </motion.button>
          </div>
        </div>

        {/* 3. CONTENT AREA (GRID VS TABLE) */}
        {isLoading ? (
          <div className="flex justify-center items-center py-16">
            <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : outlets.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm">
            <FaBuilding className="w-12 h-12 mx-auto mb-2 text-gray-300 stroke-1" />
            <p className="font-semibold text-gray-600">Belum ada outlet terdaftar</p>
            <p className="text-xs text-gray-400 mt-1">Klik tombol 'Tambah Outlet' untuk menambahkan data baru.</p>
          </div>
        ) : viewMode === "grid" ? (
          /* GRID CARD VIEW */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {page.map((row) => {
              prepareRow(row);
              const item = row.original;
              const isAct =
                item.status === "active" ||
                item.status === "Aktif" ||
                item.status === "Active";

              return (
                <motion.div
                  key={item.id}
                  whileHover={{ y: -4 }}
                  className="bg-white rounded-3xl p-5 border border-gray-100 shadow-xl shadow-gray-200/40 flex flex-col justify-between space-y-4 hover:border-teal-200 transition-all duration-300 relative overflow-hidden"
                >
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-teal-50 text-teal-600 font-extrabold flex items-center justify-center shadow-sm">
                          <FaStore className="w-5 h-5" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 text-base leading-tight">
                            {item.name}
                          </h3>
                          <span className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                            <FiMapPin className="w-3 h-3 text-teal-600" />
                            {item.kota || "Kota belum diset"}
                          </span>
                        </div>
                      </div>

                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                          isAct
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : "bg-rose-50 text-rose-700 border border-rose-200"
                        }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${
                            isAct ? "bg-emerald-500" : "bg-rose-500"
                          }`}
                        />
                        {isAct ? "Aktif" : "Inaktif"}
                      </span>
                    </div>

                    <div className="space-y-2 pt-2 text-xs text-gray-600 border-t border-gray-50">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Manager Outlet</span>
                        <span className="font-bold text-gray-800">
                          {item.manager?.name || item.manager?.full_name || "-"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">No. Telepon</span>
                        <span className="font-mono text-gray-700">
                          {item.phone || "-"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Email</span>
                        <span className="text-gray-700 truncate max-w-[160px]">
                          {item.email || "-"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => handleDetail(item)}
                      className="flex-1 py-2 px-3 rounded-xl bg-teal-50 hover:bg-teal-100 text-teal-700 text-xs font-bold transition-all flex items-center justify-center gap-1.5"
                    >
                      <FiEye className="w-3.5 h-3.5" />
                      <span>Detail</span>
                    </button>
                    <button
                      onClick={() => handleEdit(item)}
                      className="p-2 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-600 transition-all"
                      title="Edit"
                    >
                      <FiEdit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(item)}
                      className="p-2 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-600 transition-all"
                      title="Hapus"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          /* TABLE VIEW */
          <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
            <table
              {...getTableProps()}
              className="w-full text-left text-xs text-gray-600"
            >
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr
                    {...headerGroup.getHeaderGroupProps()}
                    className="bg-gray-50 text-gray-400 font-bold uppercase tracking-wider text-[10px]"
                  >
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps()} className="p-3.5 border-b border-gray-100">
                        {column.render("Header")}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()} className="divide-y divide-gray-100">
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <tr
                      {...row.getRowProps()}
                      className="hover:bg-teal-50/30 transition-colors"
                    >
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()} className="p-3.5">
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* PAGINATION */}
        <div className="flex flex-col sm:flex-row items-center justify-between pt-4 border-t border-gray-100 gap-3 text-xs">
          <p className="text-gray-400">
            Menampilkan <span className="font-bold text-gray-700">{page.length}</span> dari{" "}
            <span className="font-bold text-gray-700">{rows.length}</span> outlet
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={previousPage}
              disabled={!canPreviousPage}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                !canPreviousPage
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-teal-50 hover:text-teal-600 hover:border-teal-200"
              }`}
            >
              <FiChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs font-semibold text-gray-600 px-2">
              Halaman {pageIndex + 1} dari {pageOptions.length}
            </span>
            <button
              onClick={nextPage}
              disabled={!canNextPage}
              className={`p-2 rounded-xl border flex items-center gap-1 font-semibold transition-all ${
                !canNextPage
                  ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-teal-50 hover:text-teal-600 hover:border-teal-200"
              }`}
            >
              <FiChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>

      {/* 4. SIDE DETAIL SLIDE-OVER DRAWER */}
      <AnimatePresence>
        {isDetailVisible && selectedOutlet && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeDetail}
              className="fixed inset-0 bg-gray-900/40 backdrop-blur-xs z-40"
            />

            {/* Slide Drawer */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 250 }}
              className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 p-6 overflow-y-auto flex flex-col justify-between border-l border-gray-100"
            >
              <div className="space-y-6">
                {/* Header Drawer */}
                <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-teal-600 text-white font-extrabold flex items-center justify-center shadow-lg shadow-teal-600/20">
                      <FaStore className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">Detail Outlet</h2>
                      <span className="text-xs text-gray-400">ID Outlet: #{selectedOutlet.id}</span>
                    </div>
                  </div>
                  <button
                    onClick={closeDetail}
                    className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                </div>

                {/* Outlet Main Info */}
                <div className="p-4 rounded-3xl bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-100 space-y-3">
                  <div className="flex justify-between items-center">
                    <h3 className="text-xl font-extrabold text-gray-900">{selectedOutlet.name}</h3>
                    <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 font-bold text-xs">
                      Active
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 flex items-center gap-1.5">
                    <FiMapPin className="text-teal-600 w-3.5 h-3.5" />
                    {selectedOutlet.kota || "Kota tidak dispesifikasikan"}
                  </p>
                </div>

                {/* Specs List */}
                <div className="space-y-3 text-xs">
                  <div className="p-3.5 rounded-2xl bg-gray-50 border border-gray-100 flex items-center gap-3">
                    <FiPhone className="text-teal-600 w-4 h-4" />
                    <div>
                      <p className="text-gray-400 font-medium">Nomor Telepon</p>
                      <p className="font-bold text-gray-800">{selectedOutlet.phone || "-"}</p>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-gray-50 border border-gray-100 flex items-center gap-3">
                    <FiMail className="text-teal-600 w-4 h-4" />
                    <div>
                      <p className="text-gray-400 font-medium">Alamat Email</p>
                      <p className="font-bold text-gray-800">{selectedOutlet.email || "-"}</p>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-gray-50 border border-gray-100 flex items-center gap-3">
                    <FiUser className="text-teal-600 w-4 h-4" />
                    <div>
                      <p className="text-gray-400 font-medium">Manajer Outlet</p>
                      <p className="font-bold text-gray-800">
                        {selectedOutlet.manager?.name || selectedOutlet.manager?.full_name || "-"}
                      </p>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-2xl bg-gray-50 border border-gray-100">
                    <p className="text-gray-400 font-medium mb-1">Alamat Lengkap</p>
                    <p className="font-semibold text-gray-800 leading-relaxed">
                      {typeof selectedOutlet.address === "string"
                        ? selectedOutlet.address
                        : JSON.stringify(selectedOutlet.address || "Tidak ada alamat")}
                    </p>
                  </div>
                </div>

                {/* Quick Modal Triggers */}
                <div className="space-y-2.5 pt-2">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Aksi Cepat</p>
                  <button
                    onClick={showShiftDetail}
                    className="w-full p-3 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold text-xs transition-all flex items-center justify-between"
                  >
                    <span className="flex items-center gap-2">
                      <FiClock className="text-blue-600" />
                      Lihat Shift Karyawan
                    </span>
                    <FiChevronRight className="text-gray-400" />
                  </button>

                  <button
                    onClick={handleOpenEmployeeModal}
                    className="w-full p-3 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold text-xs transition-all flex items-center justify-between"
                  >
                    <span className="flex items-center gap-2">
                      <FiUsers className="text-emerald-600" />
                      Lihat Daftar Karyawan
                    </span>
                    <FiChevronRight className="text-gray-400" />
                  </button>

                  <button
                    onClick={handleOpenDrugModal}
                    className="w-full p-3 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold text-xs transition-all flex items-center justify-between"
                  >
                    <span className="flex items-center gap-2">
                      <FiPackage className="text-purple-600" />
                      Lihat Stok Obat Outlet
                    </span>
                    <FiChevronRight className="text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Drawer Footer Edit Button */}
              <div className="pt-6 border-t border-gray-100">
                <button
                  onClick={() => handleEdit(selectedOutlet)}
                  className="w-full py-3 rounded-2xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs shadow-lg shadow-amber-500/20 transition-all flex items-center justify-center gap-2"
                >
                  <FiEdit3 className="w-4 h-4" />
                  <span>Edit Data Outlet</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* 5. SHIFT MODAL DIALOG */}
      <AnimatePresence>
        {isShiftVisible && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.25 }}
              className="bg-white p-6 rounded-3xl shadow-2xl w-full max-w-5xl overflow-hidden max-h-[90vh] flex flex-col justify-between space-y-5"
            >
              <div>
                <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-2xl bg-blue-50 text-blue-600 border border-blue-100">
                      <FiCalendar className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">
                        Jadwal Shift Karyawan - {selectedOutlet?.name}
                      </h2>
                      <p className="text-xs text-gray-400">Pilih periode bulan untuk melihat matrik shift</p>
                    </div>
                  </div>
                  <button
                    onClick={closeShiftDetail}
                    className="p-2 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-100"
                  >
                    <FiX className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 my-4">
                  <select
                    value={selectedPeriod}
                    onChange={(e) => setSelectedPeriod(e.target.value)}
                    className="sm:col-span-2 p-2.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-teal-500/20"
                  >
                    {periods.map((period) => (
                      <option key={period.id} value={period.id}>
                        Periode: {period.year} - Bulan {period.month}
                      </option>
                    ))}
                  </select>

                  <button
                    onClick={fetchShifts}
                    className="p-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-2xl text-xs font-bold transition-all shadow-md shadow-teal-600/20"
                  >
                    Tampilkan Shift
                  </button>
                </div>

                <div className="overflow-x-auto rounded-2xl border border-gray-100 max-h-72">
                  <table className="w-full text-left text-xs text-gray-600">
                    <thead className="bg-gray-50 text-gray-400 font-bold uppercase tracking-wider text-[10px] sticky top-0 z-10">
                      <tr>
                        <th className="p-3">Karyawan</th>
                        {Array.from({ length: 30 }, (_, i) => (
                          <th key={i} className="p-2 text-center">{i + 1}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {shiftData.length === 0 ? (
                        <tr>
                          <td colSpan={31} className="py-8 text-center text-gray-400 font-medium">
                            Klik 'Tampilkan Shift' untuk memuat data
                          </td>
                        </tr>
                      ) : (
                        shiftData.map((emp) => (
                          <tr key={emp.id} className="hover:bg-teal-50/20">
                            <td className="p-3 font-bold text-gray-900 whitespace-nowrap">
                              {emp.full_name}
                            </td>
                            {Array.from({ length: 30 }, (_, i) => {
                              const shift = emp.shifts?.find((s) => new Date(s.day).getDate() === i + 1);
                              return (
                                <td key={i} className="p-2 text-center font-bold">
                                  {shift ? (
                                    <span className="px-1.5 py-0.5 rounded bg-teal-100 text-teal-800 text-[10px]">
                                      {shift.shift?.code || "S"}
                                    </span>
                                  ) : (
                                    "-"
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex gap-3 pt-3 border-t border-gray-100">
                <button
                  onClick={exportToPDF}
                  className="flex-1 py-2.5 rounded-2xl bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs flex items-center justify-center gap-2 shadow-md shadow-rose-600/20"
                >
                  <FiDownload className="w-4 h-4" />
                  <span>Export PDF</span>
                </button>
                <button
                  onClick={closeShiftDetail}
                  className="py-2.5 px-6 rounded-2xl bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold text-xs"
                >
                  Tutup
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* EMPLOYEE MODAL */}
      <EmployeeModal
        isOpen={isEmployeeModalOpen}
        onClose={() => setIsEmployeeModalOpen(false)}
        employees={employees}
        meta={employeesMeta}
        onPageChange={(page) => fetchEmployees(selectedOutlet, page)}
        onSearch={(search) => fetchEmployees(selectedOutlet, 1, search)}
      />

      {/* DRUG MODAL */}
      <DrugModal
        isOpen={isDrugModalOpen}
        onClose={() => setIsDrugModalOpen(false)}
        drugs={drugs}
        meta={drugsMeta}
        onPageChange={(page) => fetchDrugs(selectedOutlet, page)}
        onSearch={(search) => fetchDrugs(selectedOutlet, 1, search)}
      />

      {/* FILTER MODAL */}
      {isFilterOpen && (
        <FilterModal
          isOpen={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={handleApplyFilters}
        />
      )}

      {/* ACCESS DENIED OVERLAY */}
      {showAccessDenied && (
        <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white p-6 rounded-3xl shadow-2xl w-full max-w-sm text-center space-y-4">
            <h2 className="text-xl font-bold text-gray-900">Akses Ditolak</h2>
            <p className="text-xs text-gray-500">
              Anda tidak memiliki akses untuk mengelola outlet.
            </p>
            <button
              onClick={() => navigate("/")}
              className="w-full py-2.5 rounded-2xl bg-teal-600 text-white text-xs font-bold hover:bg-teal-700"
            >
              Kembali ke Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutletPage;
